import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Item } from '#domains/items';
import { env } from '#lib';
import { addOrRemoveFromArray } from '#shared/utils';

type State = {
  allSelected: boolean;
  isAnyItemSelected: boolean;
  isEnabled: boolean;
  itemIds: string[];
};

type Actions = {
  enable: () => void;
  disable: () => void;
  selectAll: () => void;
  selectNone: () => void;
  toggleItemSelected: (item: Item) => void;
  isItemSelected: (item: Item) => boolean;

  // For API calls
  getPayload: () => { itemIds: string[]; invert: boolean };
};

type BulkEditStore = State & Actions;

export const useBulkEditStore = create<BulkEditStore>()(
  devtools(
    (set, get) => {
      return {
        allSelected: false,
        isAnyItemSelected: false,
        isEnabled: false,
        itemIds: [],

        enable: () => set({ isEnabled: true }),
        disable: () => set({ isEnabled: false }),

        selectAll: () => {
          set({ allSelected: true, itemIds: [], isAnyItemSelected: true });
        },

        selectNone: () => {
          set({ allSelected: false, itemIds: [], isAnyItemSelected: false });
        },

        toggleItemSelected: item => {
          const { itemIds } = get();
          const newItemIds = addOrRemoveFromArray(itemIds, item.id);
          const isAnyItemSelected = newItemIds.length > 0;
          set({ itemIds: newItemIds, isAnyItemSelected });
        },

        isItemSelected: item => {
          const { itemIds, allSelected } = get();

          if (allSelected) {
            // In "select all" mode, itemIds contains excluded items
            return !itemIds.includes(item.id);
          }

          // In normal mode, itemIds contains selected items
          return itemIds.includes(item.id);
        },

        getPayload: () => {
          const { allSelected, itemIds } = get();

          return {
            itemIds,
            invert: allSelected && itemIds.length > 0
          };
        }
      };
    },
    {
      name: 'useBulkEditStore',
      enabled: env.isDev()
    }
  )
);
